import { Injectable } from "@angular/core";
import { ICharacteristicTemplateValidationType } from "company/components/advanced-settings/validation-errors-admin/validation-errors-admin.models";
import { ConflictSuppressionRule } from "company/components/avails-conflict-engine/conflict-check-suppression/conflict-check-suppression.models";
import { IEntityStatus } from "../../models/i-entity-status";
import { IRecIdIsValid } from "../../models/i-rec-id-is-valid";
import { RlHttpClient } from "../rl-http-client.service";
import { IAvailsSiblingInheritanceRelevancyConfig, IConflictCheckConfig, IConflictCheckTemplateSuppression, IConflictCheckwfStatusSuppression } from "./admin-config.models";

@Injectable({ providedIn: "root" })
export class AdminConfigService {

	constructor(private readonly _httpClient: RlHttpClient) { }

	getTemplateStatuses(charType: number, templateType: number) {
		return this._httpClient
			.get("/admin/characteristic-template-status-list")
			.params({
				charID: charType,
				templateID: templateType
			})
			.fetch<IEntityStatus[]>();
	}

	getValidStatuses(ids: string[]) {
		return this._httpClient
			.post(`/admin/valid-statuses`)
			.body(ids)
			.fetch<{ [charTypeId: number]: IEntityStatus[] }>();
	}

	validateBulkUpdateStatusRecords(charTypeId: number, statusId: number, templateId: number, recIds: string) {
		return this._httpClient
			.post("/admin/validate-bulk-status-update-records")
			.body({
				charTypeId: charTypeId,
				recIds: recIds,
				statusId: statusId,
				templateId: templateId
			})
			.fetch<IRecIdIsValid[]>();
	}

	bulkUpdate(charTypeId: number, recIds: string, statusId: number, statusLabel: string, templateId: number) {
		return this._httpClient
			.post("/admin/update-record-status-bulk")
			.body({
				charTypeId: charTypeId,
				recIds: recIds,
				templateId: templateId,
				statusId: statusId,
				statusLabel: statusLabel
			})
			.fetch<string>();
	}

	updateCharacteristicTemplateValidationType(charTypeId: number, templateIds: string, validationType: number) {
		return this._httpClient
			.post("/admin/update-validation-errors")
			.body({
				charTypeId,
				templateIds,
				validationType
			})
			.fetch<string>();
	}

	getCharacteristicTemplateListForValidationErrorConfig() {
		return this._httpClient
			.get("/admin/validation-errors")
			.fetch<ICharacteristicTemplateValidationType[]>();
	}

	getConflictCheckConfig() {
		return this._httpClient
			.get("/admin/conflict-check-config")
			.fetch<IConflictCheckConfig>();
	}

	updateConflictCheckTemplateSuppression(suppression: IConflictCheckTemplateSuppression) {
		return this._httpClient
			.post("/admin/conflict-check-config/template-suppression")
			.body(suppression)
			.fetch<IConflictCheckConfig>();
	}

	deleteConflictCheckTemplateSuppression(charTypeId: number) {
		return this._httpClient
			.delete("/admin/conflict-check-config/template-suppression")
			.params({ charTypeId })
			.fetch<IConflictCheckConfig>();
	}

	updateConflictCheckWfStatusSuppression(suppression: IConflictCheckwfStatusSuppression) {
		return this._httpClient
			.post("/admin/conflict-check-config/wf-status-suppression")
			.body(suppression)
			.fetch<IConflictCheckConfig>();
	}

	deleteConflictCheckWfStatusSuppression(charTypeId: number, processId: number) {
		return this._httpClient
			.delete("/admin/conflict-check-config/wf-status-suppression")
			.params({ charTypeId, processId })
			.fetch<IConflictCheckConfig>();
	}

	updateConflictCheckControlFlag(key: string, value: number) {
		return this._httpClient
			.post("/admin/conflict-check-config/flag")
			.body({
				key,
				value
			})
			.fetch<IConflictCheckConfig>();
	}

	getConflictSuppressionRules(contextCharTypeId: number) {
		return this._httpClient
			.get(`/admin/conflict-suppression/${contextCharTypeId}`)
			.fetch<ConflictSuppressionRule[]>();
	}

	// setConflictSuppressionRules(contextCharTypeId: number, rules: ConflictSuppressionRule[]) {
	// 	return this._httpClient
	// 		.post(`/admin/conflict-suppression/${contextCharTypeId}`)
	// 		.body(rules)
	// 		.fetch<void>();
	// }

	createConflictSuppressionRules(contextCharTypeId: number, rules: ConflictSuppressionRule[]) {
		return this._httpClient
			.post(`/admin/conflict-suppression/${contextCharTypeId}`)
			.body(rules)
			.fetch<void>();
	}

	updateConflictSuppressionRule(contextCharTypeId: number, ruleId: string, rule: ConflictSuppressionRule) {
		return this._httpClient
			.put(`/admin/conflict-suppression/${contextCharTypeId}/${ruleId}`)
			.body(rule)
			.fetch<void>();
	}

	deleteConflictSuppressionRules(contextCharTypeId: number, ruleIds: string[]) {
		return this._httpClient
			.delete(`/admin/conflict-suppression/${contextCharTypeId}`)
			.body(ruleIds)
			.fetch<void>();
	}

	getConflictControlSettings() {
		return this._httpClient
			.get(`/admin/conflict-control-settings`)
			.fetch<{
				processRightsInOverlap: boolean,
				processRightsInOverlapNex: boolean,
				processInfoRightsOverlap: boolean
				conflictAssignment: boolean,
				alternativeLabelConflictTypes: number[],
				limitedByClearanceRightsActions: boolean,
				showHistoricalConflicts: boolean,
				exactClearanceConflicts: boolean,
				allowNriClearanceOverlap: boolean
			}>();
	}

	setConflictControlSettings(processRightsInOverlap: boolean, processRightsInOverlapNex: boolean, processInfoRightsOverlap: boolean, conflictAssignment: boolean, alternativeLabelConflictTypes: number[], limitedByClearanceRightsActions: boolean, showHistoricalConflicts: boolean, exactClearanceConflicts: boolean, allowNriClearanceOverlap: boolean) {
		return this._httpClient
			.post(`/admin/conflict-control-settings`)
			.body({
				processRightsInOverlap,
				processRightsInOverlapNex,
				processInfoRightsOverlap,
				conflictAssignment,
				alternativeLabelConflictTypes,
				limitedByClearanceRightsActions,
				showHistoricalConflicts,
				exactClearanceConflicts,
				allowNriClearanceOverlap
			})
			.fetch();
	}

	getSirConfig() {
		return this._httpClient
			.get(`/admin/sir-config`)
			.fetch<IAvailsSiblingInheritanceRelevancyConfig[]>();
	}

	setSirConfig(sirConfig: IAvailsSiblingInheritanceRelevancyConfig[]) {
		return this._httpClient
			.post(`/admin/sir-config`)
			.body(sirConfig)
			.fetch<void>();
	}

}
